<template lang="pug">
button.buttonComponent(
  type="button",
  :class="[buttonClasses, `_${variant}`]",
  :disabled="isDisabled || isLoading"
)
  .loader( v-if="isLoading" )
    ui-loader(
      :size="28"
    )
    p(v-if="loadingText") {{ loadingText }}
  .text(
    v-if="$slots.default"
    :class="textClasses"
    :style="{visibility: isLoading ? 'hidden' : 'visible'}"
  )
    slot
  .icon(v-if="withIcon")
    ui-svg-icon(
      :name="withIcon"
      :size="sizeIcon"
    )
</template>

<script>
export default {
  props: {
    isDisabled: Boolean,
    isLoading: Boolean,
    loadingText: {
      type: String,
      default: null
    },
    isResponsive: Boolean,
    withIcon: {
      type: String,
      default: ''
    },
    sizeIcon: {
      type: Number,
      default: 13
    },
    // xs: 32px | s: 40px | m: 46px | l: 50px | xl: 54px
    size: {
      type: String,
      default: 'm'
    },
    // blue | blue-outline | blue-outline-hover | bg-white | green | orange | orange-hover | blue-lecta
    color: {
      type: String,
      default: 'blue'
    },
    // default | 3d
    variant: {
      type: String,
      default: 'default'
    },
    checked: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buttonClasses () {
      return [{
        _loading: this.isLoading,
        _disabled: this.isDisabled,
        _responsive: this.isResponsive
      }, this.sizeClass, this.colorClass]
    },
    textClasses () {
      return {
        _withIcon: this.withIcon
      }
    },
    sizeClass () {
      switch (this.size) {
        case 'xs':
          return '_extra-small'
        case 's':
          return '_small'
        case 'm':
          return '_medium'
        case 'l':
          return '_large'
        case 'xl':
          return '_extra-large'
        default:
          return '_medium'
      }
    },
    colorClass () {
      switch (this.color) {
        case 'blue-lecta':
          return '_blue-lecta'
        case 'blue-outline':
          return '_blue-outline'
        case 'blue-outline-hover':
          return '_blue-outline-hover'
        case 'green':
          return '_green'
        case 'orange':
          return '_orange'
        case 'orange-hover':
          return '_orange-hover'
        case 'bg-white':
          if (this.checked) return '_bg-dark-blue'
          return '_bg-white'
        default:
          return '_blue'
      }
    }
  }
}
</script>

<style lang="sass" scoped>
.buttonComponent
  width: fit-content
  display: flex
  align-items: center
  justify-content: center
  border-radius: 2.5*$u
  border: none
  user-select: none
  transition: .1s linear
  padding: 2.5*$u 4*$u
  @include font('t16-medium')

  > .text
    white-space: nowrap
    display: flex
    align-items: center
  .icon
    margin-left: -$u

  > .loader
    position: absolute
    display: flex
    align-items: center
    @include gap(2*$u)
    @include font('t16-demibold')

  &._extra-small
    height: 8*$u
    padding: 0 3*$u

  &._small
    height: 10*$u
    padding: 0 3*$u

  &._medium
    height: 11.5*$u
    padding: 0 4*$u
    @media (max-width: $padWidth)
      height: 10.5*$u
      padding: 0 3*$u

  &._large
    height: 12.5*$u
    padding: 0 3.75*$u

  &._extra-large
    height: 13*$u
    padding: 0 4.5*$u

  &._responsive
    width: 100%
    justify-content: center

  &._disabled
    background-color: $cDarkGrey !important

  &._disabled:hover,
  &._disabled:active
    cursor: not-allowed

  &._blue
    background-color: $cBlue
    color: $cWhite
    &:active
      background-color: $cDarkBlue
  &._blue-lecta
    background-color: $lectaBlue
    color: $cWhite
    &:active
      background-color: $cDarkBlue

    &._3d
      border: 0.75*$u solid $cBlue
      background-color: $cBlue
      border-radius: $brM
      color: $cWhite
      box-shadow: 0 1.25*$u 0 $cDarkBlue
      .text
        @include font('t18-demibold')
      &:hover
        color: $cBlue
        background-color: $cWhite
      &:active
        color: $cBlue
        background-color: $cWhite
        box-shadow: none
        transform: translateY(0.75*$u)

  &._green
    background-color: $cGreen
    color: $cWhite
    &:active
      background-color: $cDarkGreen

  &._orange
    background-color: $cOrange
    color: $cWhite

  &._orange-hover
    background-color: $cOrange
    color: $cWhite
    &:hover, &:active
      background-color: $cDarkBlue

  &._blue-outline
    border: 0.75*$u solid $cBlue
    background-color: $cWhite
    color: $cBlue
    &:active
      border-color: $cDarkBlue
      color: $cDarkBlue

    &._3d
      border: 0.75*$u solid $cBlue
      background-color: $cWhite
      border-radius: $brM
      color: $cBlue
      box-shadow: 0 1.25*$u 0 $cDarkBlue
      .text
        @include font('t18-demibold')
      &:hover
        color: $cWhite
        background-color: $cBlue
      &:active
        color: $cWhite
        background-color: $cBlue
        box-shadow: none
        transform: translateY(0.75*$u)

  &._blue-outline-hover
    border: 0.75*$u solid $cBlue
    background-color: $cWhite
    color: $cBlue
    &:hover, &:active
      border-color: $cDarkBlue
      background-color: $cDarkBlue
      color: $cWhite

  &._bg-white
    background-color: $cWhite
    color: $cBlue
    border-radius: 7.5*$u
    .icon
      color: $cBlue
  &._bg-dark-blue
    background-color: $cDarkBlue
    color: $cWhite
    border-radius: 7.5*$u
    .icon
      color: $cWhite

  &:hover
    box-shadow: 0px 7px 15px rgba(37, 64, 142, 0.2)
</style>
